// ===========================================
// BUSTED! BLACKJACK - PIXI.JS CONTROL PANEL
// ===========================================
// Pure PixiJS implementation of the control panel.
// Pixel-perfect replication of React ControlPanel.
// Renders inside game canvas, no React boundaries.
// ===========================================
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  ARCHITECTURE: SERVER-AUTHORITATIVE BUTTON STATES                        ║
// ║                                                                          ║
// ║  This control panel does NOT contain game logic. Button enable/disable   ║
// ║  states come from the server via CONTROLS_UPDATED events.                ║
// ║                                                                          ║
// ║  The client is a "dumb terminal" - it renders what the server says.      ║
// ║                                                                          ║
// ║  See: CLIENT_ARCHITECTURE.md for full documentation.                     ║
// ║  See: backend/src/pare/ for the Player Action Rules Engine (server-only) ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//

import { Container, Graphics, Text, TextStyle, FederatedPointerEvent, Ticker, FillGradient } from 'pixi.js';
import { useGameStateStore } from '../../../stores/gameStateStore';
import { useAuthStore } from '../../../stores/authStore';
import { useStagedChipsStore } from '../../../stores/stagedChipsStore';
import { useCardTrackingStore } from '../../../stores/cardTrackingStore';
import { useSocketStore } from '../../../stores/socketStore';
import { useAnimationQueueStore } from '../../../stores/animationQueueStore';
import { GamePhase, SeatControlState } from '@busted-blackjack/shared';
import { getBasicStrategy, type StrategyAction } from '../../../utils/basicStrategy';

// ===========================================
// TYPES
// ===========================================

export type ControlPanelPosition =
  | 'viewport-left'
  | 'table-left'
  | 'table-right'
  | 'viewport-right'
  | 'top'
  | 'bottom';

export interface PixiControlPanelConfig {
  /** Table ID for position persistence */
  tableId: string;
  /** Callback when position changes */
  onPositionChange?: (position: ControlPanelPosition) => void;
  /** Action handlers */
  onHit: () => void;
  onStand: () => void;
  onDouble: () => void;
  onSplit: () => void;
  onSurrender: () => void;
  onBet: () => void;
  onAutobet: () => void;
  /** Insurance action handlers */
  onInsuranceAccept: (amount: number) => void;
  onInsuranceDecline: () => void;
  /** Canvas dimensions for positioning */
  canvasWidth: number;
  canvasHeight: number;
  /** Table bounds for positioning */
  tableBounds: { x: number; y: number; width: number; height: number };
  /** Scale factor for responsive sizing */
  scale: number;
  /** Text size overrides for buttons */
  textSizes?: {
    mainButtonsVert?: number;   // Main action buttons (HIT/STAND/etc) in vertical layout
    mainButtonsHoriz?: number;  // Main action buttons in horizontal layout
    sideButtonsVert?: number;   // Side buttons (BET/AUTOBET) in vertical layout
    sideButtonsHoriz?: number;  // Side buttons in horizontal layout
  };
  /** Socket connection state - disables bet button when not connected */
  isConnected?: boolean;
}

interface ButtonConfig {
  label: string;
  action: string;
  gradientFrom: number;
  gradientTo: number;
  textColor: number;
  size: 'small' | 'large';
}

interface ButtonState {
  graphics: Graphics;
  text: Text;
  neonBorder: Graphics | null;
  config: ButtonConfig;
  enabled: boolean;
  recommended: boolean;
  isDeviation: boolean;
}

// ===========================================
// CONSTANTS
// ===========================================

// Button dimensions
const BUTTON_PADDING = 8;
const BUTTON_RADIUS = 10;
const SMALL_BUTTON_HEIGHT = 64;  // Doubled from 32
const LARGE_BUTTON_HEIGHT = 52;
const BUTTON_GAP = 6;

// Horizontal layout (bottom)
const HORIZONTAL_PANEL_WIDTH = 600;
const HORIZONTAL_PANEL_PADDING = 16;
const SIDE_STACK_WIDTH = 128; // Matches vertical button width (VERTICAL_PANEL_WIDTH - HANDLE_CONTAINER_WIDTH * 2)

// Vertical layout (sides) - matches React control panel width
const VERTICAL_PANEL_WIDTH = 160;
const VERTICAL_PANEL_PADDING = 10;

// Horizontal layout height matches vertical layout width for visual consistency
const HORIZONTAL_PANEL_HEIGHT = 160;

// Handle dimensions - sleek but visible bars at panel edges
// Vertical handles: 16x70px container, 6x48px visible bar (fatter than before)
// Bottom handle: 140x16px container, 96x6px visible bar
const HANDLE_WIDTH = 16;  // Space reserved for handle in button layout
const HANDLE_CONTAINER_WIDTH = 16;
const HANDLE_CONTAINER_HEIGHT = 70;
const HANDLE_BAR_WIDTH = 6;
const HANDLE_BAR_HEIGHT = 48;
const BOTTOM_HANDLE_CONTAINER_WIDTH = 140;
const BOTTOM_HANDLE_CONTAINER_HEIGHT = 16;
const BOTTOM_HANDLE_BAR_WIDTH = 96;
const BOTTOM_HANDLE_BAR_HEIGHT = 6;

// Colors
const PANEL_BG_COLOR = 0x000000;
const PANEL_BG_ALPHA = 1.0;  // 100% opaque
const HANDLE_COLOR = 0x808080;
const HANDLE_ALPHA = 0.5;
const DISABLED_ALPHA = 0.4;

// Neon border colors (matching CSS neon-border rainbow animation)
const NEON_BORDER_WIDTH = 6;
const NEON_GLOW_ALPHA = 0.8;
const NEON_ANIMATION_SPEED = 0.01; // Diagonal flow speed (reduced 50%)

// Rainbow colors for neon border animation (matching CSS gradient)
const NEON_RAINBOW_COLORS = [
  0xff0080, // pink
  0xff00ff, // magenta
  0x8000ff, // purple
  0x0080ff, // blue
  0x00ffff, // cyan
  0x00ff80, // green-cyan
  0x00ff00, // green
  0x80ff00, // yellow-green
  0xffff00, // yellow
  0xff8000, // orange
];

// Deviation uses magenta-purple range
const NEON_DEVIATION_COLORS = [
  0xff00ff, // magenta
  0xff00aa, // pink-magenta
  0xff0080, // pink
  0xff00aa, // pink-magenta
];

// Button gradient colors
const BUTTON_COLORS: Record<string, { from: number; to: number; text: number }> = {
  SPLIT: { from: 0xef4444, to: 0xf97316, text: 0xffffff },
  DOUBLE: { from: 0xef4444, to: 0xf97316, text: 0xffffff },
  HIT: { from: 0xf97316, to: 0xfbbf24, text: 0x000000 },
  BET: { from: 0xfbbf24, to: 0xfb923c, text: 0x000000 },
  STAND: { from: 0x9333ea, to: 0x3b82f6, text: 0xffffff },
  SURRENDER: { from: 0x1d4ed8, to: 0x7c3aed, text: 0x93c5fd },
  AUTOBET: { from: 0xc026d3, to: 0x7c3aed, text: 0xffffff },
};

// Session storage key prefix
const POSITION_STORAGE_PREFIX = 'pixi-control-panel-position-';

// ===========================================
// POSITION PERSISTENCE
// ===========================================

export function getStoredPosition(tableId: string): ControlPanelPosition | null {
  try {
    const key = POSITION_STORAGE_PREFIX + tableId;
    const stored = sessionStorage.getItem(key);
    if (stored && ['viewport-left', 'table-left', 'table-right', 'viewport-right', 'top', 'bottom'].includes(stored)) {
      return stored as ControlPanelPosition;
    }
  } catch (e) {
    // sessionStorage not available
  }
  return null;
}

function storePosition(tableId: string, position: ControlPanelPosition): void {
  try {
    const key = POSITION_STORAGE_PREFIX + tableId;
    sessionStorage.setItem(key, position);
  } catch (e) {
    // sessionStorage not available
  }
}

// ===========================================
// PIXI CONTROL PANEL CLASS
// ===========================================

export class PixiControlPanel extends Container {
  private config: PixiControlPanelConfig;
  private panelPosition: ControlPanelPosition;
  private background: Graphics;
  private buttons: Map<string, ButtonState> = new Map();
  private leftHandle: Graphics | null = null;
  private rightHandle: Graphics | null = null;
  private bottomHandle: Graphics | null = null;
  private topHandle: Graphics | null = null;

  // Store subscriptions
  private unsubscribers: (() => void)[] = [];

  // Cached state for diffing
  private cachedAvailableActions: string[] = [];
  private cachedGamePhase: GamePhase | null = null;
  private cachedStagedAmount: number = 0;
  private cachedBalance: number = 0;
  private cachedCurrentBet: number = 0;
  private cachedCurrentTurnSeat: number | null = null;
  private cachedCurrentHandIndex: number | null = null;
  private cachedMySeatPosition: number | null = null;
  private cachedTurnSignal: number = 0;

  // Recommendation state
  private cachedRecommendedAction: StrategyAction | null = null;
  private cachedIsDeviation: boolean = false;
  private cachedShowRecommendations: boolean = false;

  // Autobet state
  private cachedAutobetEnabled: boolean = false;

  // Auto-play state (imperative, no React race conditions)
  private autoPlayEnabled: boolean = false;
  private autoPlayTimer: ReturnType<typeof setTimeout> | null = null;
  private autoPlayActionExecutedThisTurn: boolean = false;
  private autoPlayLastTurnKey: string = ''; // "seatPosition-handIndex" to detect turn changes

  // Insurance auto-play state
  private cachedInsuranceOffered: boolean = false;
  private cachedInsuranceDecisionMade: boolean = false;
  private insuranceAutoPlayTimer: ReturnType<typeof setTimeout> | null = null;
  private cachedInsuranceRecommendation: 'ACCEPT' | 'DECLINE' | null = null;

  // Socket connection state
  private cachedIsConnected: boolean = true;

  // ===========================================
  // SERVER-AUTHORITATIVE CONTROL PANEL STATE
  // ===========================================
  // Server broadcasts the exact button states. Client disables on click,
  // re-enables per server instructions. This eliminates all client-side derivation.
  private cachedServerControls: SeatControlState | null = null;
  // Flag to prevent button re-enabling until server responds with CONTROLS_UPDATED
  // Set true on action click, set false when CONTROLS_UPDATED received
  private awaitingServerControls: boolean = false;

  // Animation completion state (for early BET enablement)
  private cachedMyHandAnimationsComplete: boolean = true;
  private cachedHandHasOutcome: boolean = false;

  // Neon border animation
  private neonAnimationPhase: number = 0;
  private neonAnimationTicker: Ticker | null = null;

  // Button text that may change
  private autobetButtonText: Text | null = null;
  private betButtonText: Text | null = null;
  private betButtonSecondaryText: Text | null = null; // "AUTOBET" text shown below "STOP"

  // Keyboard handler for arrow key panel movement
  private boundKeyboardHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(config: PixiControlPanelConfig) {
    super();
    this.config = config;
    this.eventMode = 'static';

    // Initialize connection state from config (default to true if not provided)
    this.cachedIsConnected = config.isConnected ?? true;

    // Load position from storage or default to bottom
    this.panelPosition = getStoredPosition(config.tableId) || 'bottom';

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Build initial layout
    this.buildLayout();

    // Subscribe to stores
    this.subscribeToStores();

    // Initial position update
    this.updatePanelPosition();

    // Start neon border animation ticker
    this.startNeonAnimation();

    // Set up keyboard handler for arrow keys
    this.setupKeyboardHandler();
  }

  // ===========================================
  // KEYBOARD NAVIGATION
  // ===========================================

  private setupKeyboardHandler(): void {
    this.boundKeyboardHandler = (e: KeyboardEvent) => {
      // Only handle arrow keys
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;

      // Ignore if user is typing in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      e.preventDefault();

      const isHorizontal = this.panelPosition === 'top' || this.panelPosition === 'bottom';
      const verticalPositions: ControlPanelPosition[] = ['viewport-left', 'table-left', 'table-right', 'viewport-right'];
      const currentVerticalIdx = verticalPositions.indexOf(this.panelPosition);

      switch (e.key) {
        case 'ArrowUp':
          if (this.panelPosition === 'bottom') {
            this.setPosition('top');
          } else if (!isHorizontal) {
            // From vertical position, go to top
            this.setPosition('top');
          }
          break;

        case 'ArrowDown':
          if (this.panelPosition === 'top') {
            this.setPosition('bottom');
          } else if (!isHorizontal) {
            // From vertical position, go to bottom
            this.setPosition('bottom');
          }
          break;

        case 'ArrowLeft':
          if (isHorizontal) {
            // From top/bottom, go to table-left
            this.setPosition('table-left');
          } else if (currentVerticalIdx > 0) {
            // Cycle through vertical positions to the left
            this.setPosition(verticalPositions[currentVerticalIdx - 1]);
          }
          break;

        case 'ArrowRight':
          if (isHorizontal) {
            // From top/bottom, go to table-right
            this.setPosition('table-right');
          } else if (currentVerticalIdx < verticalPositions.length - 1) {
            // Cycle through vertical positions to the right
            this.setPosition(verticalPositions[currentVerticalIdx + 1]);
          }
          break;
      }
    };

    window.addEventListener('keydown', this.boundKeyboardHandler);
  }

  private removeKeyboardHandler(): void {
    if (this.boundKeyboardHandler) {
      window.removeEventListener('keydown', this.boundKeyboardHandler);
      this.boundKeyboardHandler = null;
    }
  }

  // ===========================================
  // NEON BORDER ANIMATION
  // ===========================================

  private startNeonAnimation(): void {
    if (this.neonAnimationTicker) return;

    this.neonAnimationTicker = new Ticker();
    this.neonAnimationTicker.add(() => {
      this.neonAnimationPhase += NEON_ANIMATION_SPEED;
      if (this.neonAnimationPhase > 1) this.neonAnimationPhase -= 1;
      this.animateNeonBorders();
    });
    this.neonAnimationTicker.start();
  }

  private stopNeonAnimation(): void {
    if (this.neonAnimationTicker) {
      this.neonAnimationTicker.stop();
      this.neonAnimationTicker.destroy();
      this.neonAnimationTicker = null;
    }
  }

  private getNeonColor(phase: number, isDeviation: boolean): number {
    const colors = isDeviation ? NEON_DEVIATION_COLORS : NEON_RAINBOW_COLORS;
    const index = phase * colors.length;
    const i1 = Math.floor(index) % colors.length;
    const i2 = (i1 + 1) % colors.length;
    const t = index - Math.floor(index);

    // Interpolate between colors
    const c1 = colors[i1];
    const c2 = colors[i2];
    const r = Math.round(((c1 >> 16) & 0xff) * (1 - t) + ((c2 >> 16) & 0xff) * t);
    const g = Math.round(((c1 >> 8) & 0xff) * (1 - t) + ((c2 >> 8) & 0xff) * t);
    const b = Math.round((c1 & 0xff) * (1 - t) + (c2 & 0xff) * t);
    return (r << 16) | (g << 8) | b;
  }

  /**
   * Draw multi-colored gradient neon border at 45-degree angle
   * Creates a rainbow gradient effect that flows diagonally across the border
   */
  private animateNeonBorders(): void {
    this.buttons.forEach((button) => {
      if (button.recommended && button.neonBorder && button.neonBorder.visible) {
        const scale = this.config.scale;
        const g = button.graphics;
        const colors = button.isDeviation ? NEON_DEVIATION_COLORS : NEON_RAINBOW_COLORS;
        const numColors = colors.length;

        button.neonBorder.clear();

        // Calculate button dimensions with border
        const borderOffset = NEON_BORDER_WIDTH * scale * 0.5;
        const left = g.x - borderOffset;
        const top = g.y - borderOffset;
        const width = g.width + NEON_BORDER_WIDTH * scale;
        const height = g.height + NEON_BORDER_WIDTH * scale;
        const radius = (BUTTON_RADIUS * scale) + borderOffset;

        // For 45-degree gradient, color is based on (x + y) position
        // Diagonal length spans from top-left (0) to bottom-right (width + height)
        const diagonalLength = width + height;

        // Helper to interpolate between two colors
        const lerpColor = (c1: number, c2: number, t: number): number => {
          const r1 = (c1 >> 16) & 0xff;
          const g1 = (c1 >> 8) & 0xff;
          const b1 = c1 & 0xff;
          const r2 = (c2 >> 16) & 0xff;
          const g2 = (c2 >> 8) & 0xff;
          const b2 = c2 & 0xff;
          const r = Math.round(r1 + (r2 - r1) * t);
          const g = Math.round(g1 + (g2 - g1) * t);
          const b = Math.round(b1 + (b2 - b1) * t);
          return (r << 16) | (g << 8) | b;
        };

        // Helper to get smoothly interpolated color at a diagonal position (0 to 1)
        const getColorAtDiagonal = (diagPos: number): number => {
          // Add animation phase to create flowing effect
          const adjustedPos = ((diagPos + this.neonAnimationPhase) % 1 + 1) % 1;
          const scaledPos = adjustedPos * numColors;
          const index1 = Math.floor(scaledPos) % numColors;
          const index2 = (index1 + 1) % numColors;
          const t = scaledPos - Math.floor(scaledPos); // Fractional part for interpolation
          return lerpColor(colors[index1], colors[index2], t);
        };

        // Helper to get diagonal position (0-1) from x,y coordinates
        const getDiagonalPos = (x: number, y: number): number => {
          return ((x - left) + (y - top)) / diagonalLength;
        };

        // Draw border segments with colors based on diagonal position
        const numSegments = 50; // More segments = smoother gradient

        // TOP EDGE: left+radius to left+width-radius
        const topEdgeStart = left + radius;
        const topEdgeEnd = left + width - radius;
        const topEdgeLength = topEdgeEnd - topEdgeStart;
        const topSegmentWidth = topEdgeLength / numSegments;

        for (let i = 0; i < numSegments; i++) {
          const segStart = topEdgeStart + i * topSegmentWidth;
          const segEnd = segStart + topSegmentWidth;
          const midX = (segStart + segEnd) / 2;
          const color = getColorAtDiagonal(getDiagonalPos(midX, top));

          button.neonBorder.moveTo(segStart, top);
          button.neonBorder.lineTo(segEnd, top);
          button.neonBorder.stroke({ color, width: NEON_BORDER_WIDTH * scale, alpha: NEON_GLOW_ALPHA });
        }

        // BOTTOM EDGE: left+radius to left+width-radius
        const bottomY = top + height;
        for (let i = 0; i < numSegments; i++) {
          const segStart = topEdgeStart + i * topSegmentWidth;
          const segEnd = segStart + topSegmentWidth;
          const midX = (segStart + segEnd) / 2;
          const color = getColorAtDiagonal(getDiagonalPos(midX, bottomY));

          button.neonBorder.moveTo(segStart, bottomY);
          button.neonBorder.lineTo(segEnd, bottomY);
          button.neonBorder.stroke({ color, width: NEON_BORDER_WIDTH * scale, alpha: NEON_GLOW_ALPHA });
        }

        // LEFT EDGE: top+radius to top+height-radius
        const leftEdgeStart = top + radius;
        const leftEdgeEnd = top + height - radius;
        const leftEdgeLength = leftEdgeEnd - leftEdgeStart;
        const leftSegmentHeight = leftEdgeLength / numSegments;

        for (let i = 0; i < numSegments; i++) {
          const segStart = leftEdgeStart + i * leftSegmentHeight;
          const segEnd = segStart + leftSegmentHeight;
          const midY = (segStart + segEnd) / 2;
          const color = getColorAtDiagonal(getDiagonalPos(left, midY));

          button.neonBorder.moveTo(left, segStart);
          button.neonBorder.lineTo(left, segEnd);
          button.neonBorder.stroke({ color, width: NEON_BORDER_WIDTH * scale, alpha: NEON_GLOW_ALPHA });
        }

        // RIGHT EDGE: top+radius to top+height-radius
        const rightX = left + width;
        for (let i = 0; i < numSegments; i++) {
          const segStart = leftEdgeStart + i * leftSegmentHeight;
          const segEnd = segStart + leftSegmentHeight;
          const midY = (segStart + segEnd) / 2;
          const color = getColorAtDiagonal(getDiagonalPos(rightX, midY));

          button.neonBorder.moveTo(rightX, segStart);
          button.neonBorder.lineTo(rightX, segEnd);
          button.neonBorder.stroke({ color, width: NEON_BORDER_WIDTH * scale, alpha: NEON_GLOW_ALPHA });
        }

        // CORNERS - draw arcs with gradient colors
        const cornerSegments = 16;

        // Top-left corner (PI to PI*1.5)
        for (let i = 0; i < cornerSegments; i++) {
          const startAngle = Math.PI + (i * Math.PI * 0.5) / cornerSegments;
          const endAngle = Math.PI + ((i + 1) * Math.PI * 0.5) / cornerSegments;
          const midAngle = (startAngle + endAngle) / 2;
          const cx = left + radius;
          const cy = top + radius;
          const midX = cx + Math.cos(midAngle) * radius;
          const midY = cy + Math.sin(midAngle) * radius;
          const color = getColorAtDiagonal(getDiagonalPos(midX, midY));

          button.neonBorder.arc(cx, cy, radius, startAngle, endAngle);
          button.neonBorder.stroke({ color, width: NEON_BORDER_WIDTH * scale, alpha: NEON_GLOW_ALPHA });
        }

        // Top-right corner (PI*1.5 to 0/2PI)
        for (let i = 0; i < cornerSegments; i++) {
          const startAngle = Math.PI * 1.5 + (i * Math.PI * 0.5) / cornerSegments;
          const endAngle = Math.PI * 1.5 + ((i + 1) * Math.PI * 0.5) / cornerSegments;
          const midAngle = (startAngle + endAngle) / 2;
          const cx = left + width - radius;
          const cy = top + radius;
          const midX = cx + Math.cos(midAngle) * radius;
          const midY = cy + Math.sin(midAngle) * radius;
          const color = getColorAtDiagonal(getDiagonalPos(midX, midY));

          button.neonBorder.arc(cx, cy, radius, startAngle, endAngle);
          button.neonBorder.stroke({ color, width: NEON_BORDER_WIDTH * scale, alpha: NEON_GLOW_ALPHA });
        }

        // Bottom-right corner (0 to PI*0.5)
        for (let i = 0; i < cornerSegments; i++) {
          const startAngle = (i * Math.PI * 0.5) / cornerSegments;
          const endAngle = ((i + 1) * Math.PI * 0.5) / cornerSegments;
          const midAngle = (startAngle + endAngle) / 2;
          const cx = left + width - radius;
          const cy = top + height - radius;
          const midX = cx + Math.cos(midAngle) * radius;
          const midY = cy + Math.sin(midAngle) * radius;
          const color = getColorAtDiagonal(getDiagonalPos(midX, midY));

          button.neonBorder.arc(cx, cy, radius, startAngle, endAngle);
          button.neonBorder.stroke({ color, width: NEON_BORDER_WIDTH * scale, alpha: NEON_GLOW_ALPHA });
        }

        // Bottom-left corner (PI*0.5 to PI)
        for (let i = 0; i < cornerSegments; i++) {
          const startAngle = Math.PI * 0.5 + (i * Math.PI * 0.5) / cornerSegments;
          const endAngle = Math.PI * 0.5 + ((i + 1) * Math.PI * 0.5) / cornerSegments;
          const midAngle = (startAngle + endAngle) / 2;
          const cx = left + radius;
          const cy = top + height - radius;
          const midX = cx + Math.cos(midAngle) * radius;
          const midY = cy + Math.sin(midAngle) * radius;
          const color = getColorAtDiagonal(getDiagonalPos(midX, midY));

          button.neonBorder.arc(cx, cy, radius, startAngle, endAngle);
          button.neonBorder.stroke({ color, width: NEON_BORDER_WIDTH * scale, alpha: NEON_GLOW_ALPHA });
        }

      }
    });
  }

  // ===========================================
  // STORE SUBSCRIPTIONS
  // ===========================================

  private subscribeToStores(): void {
    // Fetch initial state from all stores
    const gameState = useGameStateStore.getState();
    const authState = useAuthStore.getState();
    const stagedState = useStagedChipsStore.getState();

    const myPlayer = gameState.players.get(gameState.mySeatPosition ?? -1);
    this.cachedAvailableActions = [...(gameState.availableActions || [])] as string[];
    this.cachedGamePhase = gameState.phase;
    this.cachedCurrentBet = myPlayer?.confirmedBet || 0;
    this.cachedBalance = authState.user?.balance || 0;
    this.cachedStagedAmount = stagedState.stagedAmount;
    this.cachedAutobetEnabled = stagedState.autobetEnabled;
    this.cachedShowRecommendations = authState.user?.showRecommendations ?? false;
    this.autoPlayEnabled = authState.user?.autoPlay ?? false;
    this.cachedCurrentTurnSeat = gameState.currentTurnSeat;
    this.cachedCurrentHandIndex = gameState.currentHandIndex;
    this.cachedMySeatPosition = gameState.mySeatPosition;
    this.cachedTurnSignal = gameState.turnSignal;
    // Initialize turn key for auto-play
    this.autoPlayLastTurnKey = `${this.cachedCurrentTurnSeat}-${this.cachedCurrentHandIndex}`;
    // Server-authoritative controls (may be null on initial load)
    this.cachedServerControls = gameState.seatControls.get(gameState.mySeatPosition ?? -1) || null;

    console.log('[PixiControlPanel] Initial state:', {
      availableActions: this.cachedAvailableActions,
      phase: this.cachedGamePhase,
      currentBet: this.cachedCurrentBet,
      balance: this.cachedBalance,
      stagedAmount: this.cachedStagedAmount,
      autobetEnabled: this.cachedAutobetEnabled,
      showRecommendations: this.cachedShowRecommendations,
      autoPlayEnabled: this.autoPlayEnabled,
      currentTurnSeat: this.cachedCurrentTurnSeat,
      currentHandIndex: this.cachedCurrentHandIndex,
    });

    // Compute initial recommendation
    this.computeRecommendation();

    // Try initial auto-play if enabled
    if (this.autoPlayEnabled) {
      this.tryAutoPlay();
    }

    // Update button states with initial values
    this.updateButtonStates();

    // Update autobet button text based on initial state
    // This ensures correct text even if hydration hasn't happened yet
    this.updateAutobetButtonText();

    // Subscribe to game state changes
    const unsubGame = useGameStateStore.subscribe((state) => {
      const availableActions = state.availableActions || [];
      const gamePhase = state.phase;
      const mySeatPosition = state.mySeatPosition;
      const myPlayer = state.players.get(mySeatPosition ?? -1);
      const currentBet = myPlayer?.confirmedBet || 0;
      const currentTurnSeat = state.currentTurnSeat;
      const currentHandIndex = state.currentHandIndex;
      const turnSignal = state.turnSignal;
      // Check if player's hand(s) have been resolved (have outcome)
      // This is used for early BET enablement when chip animations complete
      const handHasOutcome = myPlayer?.hands?.some(h => h.outcome !== undefined) ?? false;

      // Check if anything changed
      const actionsChanged = JSON.stringify(availableActions) !== JSON.stringify(this.cachedAvailableActions);
      const phaseChanged = gamePhase !== this.cachedGamePhase;
      const betChanged = currentBet !== this.cachedCurrentBet;
      const turnChanged = currentTurnSeat !== this.cachedCurrentTurnSeat;
      const handIndexChanged = currentHandIndex !== this.cachedCurrentHandIndex;
      // Also watch mySeatPosition - needed for reconnection when seat is set after turn info
      const mySeatChanged = mySeatPosition !== this.cachedMySeatPosition;
      // Watch turnSignal - increments when action taken or card dealt during player turn
      const turnSignalChanged = turnSignal !== this.cachedTurnSignal;
      // Watch handHasOutcome - needed for early BET enablement
      const handOutcomeChanged = handHasOutcome !== this.cachedHandHasOutcome;

      // DEBUG: Log every subscription fire to diagnose ALT+TAB issue
      console.log('[PixiControlPanel] Store subscription fired:', {
        turnChanged,
        actionsChanged,
        phaseChanged,
        currentTurnSeat,
        cachedCurrentTurnSeat: this.cachedCurrentTurnSeat,
        mySeatPosition,
        availableActions: availableActions.slice(0, 3), // First 3 for brevity
        gamePhase,
      });

      if (actionsChanged || phaseChanged || betChanged || turnChanged || handIndexChanged || mySeatChanged || turnSignalChanged || handOutcomeChanged) {
        this.cachedAvailableActions = [...availableActions] as string[];
        this.cachedGamePhase = gamePhase;
        this.cachedCurrentBet = currentBet;
        this.cachedCurrentTurnSeat = currentTurnSeat;
        this.cachedCurrentHandIndex = currentHandIndex;
        this.cachedMySeatPosition = mySeatPosition;
        this.cachedTurnSignal = turnSignal;
        this.cachedHandHasOutcome = handHasOutcome;
        this.computeRecommendation();
        this.updateButtonStates();

        // SAFEGUARD 1: Clear auto-play timer when phase changes away from playing
        // This prevents stale timers from firing after the round ends
        if (phaseChanged && gamePhase !== GamePhase.PLAYING) {
          console.log('[PixiControlPanel] Phase changed to non-playing, clearing auto-play timer');
          this.clearAutoPlayTimer();
          this.autoPlayActionExecutedThisTurn = false;
        }

        // Auto-play: check for turn change and try to execute
        if (turnChanged || handIndexChanged) {
          console.log('[PixiControlPanel] Subscription: turnChanged/handIndexChanged detected, calling checkAutoPlayTurnChange');
          this.checkAutoPlayTurnChange();
        } else if (actionsChanged || turnSignalChanged) {
          console.log('[PixiControlPanel] Subscription: actionsChanged/turnSignalChanged detected, calling tryAutoPlay');
          // Actions or turnSignal changed but same turn (e.g., after HIT, new cards dealt)
          // Reset the action-executed flag so we can execute the next action
          if (this.autoPlayActionExecutedThisTurn) {
            console.log('[PixiControlPanel] Auto-play: actions/turnSignal changed, resetting executed flag');
            this.autoPlayActionExecutedThisTurn = false;
          }
          this.tryAutoPlay();
        }

        // CRITICAL FIX for ALT+TAB: If we're in playing phase, it's our turn, and we have actions,
        // always try autoplay. This catches edge cases where turn detection fails.
        if (gamePhase === GamePhase.PLAYING &&
            currentTurnSeat === mySeatPosition &&
            availableActions.length > 0 &&
            this.autoPlayEnabled &&
            !this.autoPlayTimer &&
            !this.autoPlayActionExecutedThisTurn) {
          console.log('[PixiControlPanel] Subscription: Direct turn detection - our turn with actions, calling tryAutoPlay');
          this.tryAutoPlay();
        }
      }
    });
    this.unsubscribers.push(unsubGame);

    // Subscribe to auth store for balance, showRecommendations, and autoPlay
    const unsubAuth = useAuthStore.subscribe((state) => {
      const balance = state.user?.balance || 0;
      const showRec = state.user?.showRecommendations ?? false;
      const autoPlay = state.user?.autoPlay ?? false;
      let changed = false;

      if (balance !== this.cachedBalance) {
        this.cachedBalance = balance;
        changed = true;
      }
      if (showRec !== this.cachedShowRecommendations) {
        this.cachedShowRecommendations = showRec;
        this.computeRecommendation();
        changed = true;
        // Recommendation may have changed - try auto-play
        this.tryAutoPlay();
      }
      // Auto-play state change
      if (autoPlay !== this.autoPlayEnabled) {
        console.log('[PixiControlPanel] Auto-play state changed:', this.autoPlayEnabled, '->', autoPlay);
        this.autoPlayEnabled = autoPlay;
        if (autoPlay) {
          this.tryAutoPlay();
        } else {
          this.clearAutoPlayTimer();
        }
      }
      if (changed) {
        this.updateButtonStates();
      }
    });
    this.unsubscribers.push(unsubAuth);

    // Subscribe to staged chips (amount AND autobet state)
    const unsubStaged = useStagedChipsStore.subscribe((state) => {
      let changed = false;
      if (state.stagedAmount !== this.cachedStagedAmount) {
        this.cachedStagedAmount = state.stagedAmount;
        changed = true;
      }
      if (state.autobetEnabled !== this.cachedAutobetEnabled) {
        console.log('[PixiControlPanel] Autobet state changed:', this.cachedAutobetEnabled, '->', state.autobetEnabled);
        this.cachedAutobetEnabled = state.autobetEnabled;
        this.updateAutobetButtonText();
        changed = true;
      }
      if (changed) {
        this.updateButtonStates();
      }
    });
    this.unsubscribers.push(unsubStaged);

    // Subscribe to card tracking for true count changes (affects deviations)
    // Also triggers after CARD_DEALT when new cards are added
    const unsubCards = useCardTrackingStore.subscribe(() => {
      if (this.cachedShowRecommendations) {
        const oldRecommendation = this.cachedRecommendedAction;
        this.computeRecommendation();
        this.updateButtonStates();

        // Auto-play: if recommendation changed after we executed an action, reset and try again
        const newRecommendation = this.cachedRecommendedAction;
        if (oldRecommendation !== newRecommendation) {
          if (this.autoPlayActionExecutedThisTurn) {
            console.log('[PixiControlPanel] Auto-play: recommendation changed from', oldRecommendation, 'to', newRecommendation, '- resetting flag');
            this.autoPlayActionExecutedThisTurn = false;
          }
          this.tryAutoPlay();
        }
      }

      // Also update insurance recommendation based on true count
      this.computeInsuranceRecommendation();
      this.tryInsuranceAutoPlay();
    });
    this.unsubscribers.push(unsubCards);

    // Subscribe to insurance state changes
    const unsubInsurance = useGameStateStore.subscribe((state) => {
      const insuranceOffered = state.insuranceOffered;
      const mySeatPosition = state.mySeatPosition;
      const myPlayer = state.players.get(mySeatPosition ?? -1);
      const insuranceDecided = myPlayer?.insuranceDecided ?? false;

      const offeredChanged = insuranceOffered !== this.cachedInsuranceOffered;
      const decisionChanged = insuranceDecided !== this.cachedInsuranceDecisionMade;

      if (offeredChanged || decisionChanged) {
        this.cachedInsuranceOffered = insuranceOffered;
        this.cachedInsuranceDecisionMade = insuranceDecided;

        if (offeredChanged) {
          console.log('[PixiControlPanel] Insurance offered changed:', insuranceOffered);
          if (insuranceOffered && !insuranceDecided) {
            // Insurance just offered, compute recommendation and try auto-play
            this.computeInsuranceRecommendation();
            this.tryInsuranceAutoPlay();
          } else {
            // Insurance no longer offered, clear timer
            this.clearInsuranceAutoPlayTimer();
          }
        }

        if (decisionChanged && insuranceDecided) {
          // Decision was made, clear any pending timer
          this.clearInsuranceAutoPlayTimer();
        }
      }
    });
    this.unsubscribers.push(unsubInsurance);

    // Subscribe to animation queue for early BET enablement when chips land
    const unsubAnimation = useAnimationQueueStore.subscribe((state) => {
      const myHandAnimationsComplete = state.myHandAnimationsComplete;
      if (myHandAnimationsComplete !== this.cachedMyHandAnimationsComplete) {
        console.log('[PixiControlPanel] myHandAnimationsComplete changed:', this.cachedMyHandAnimationsComplete, '->', myHandAnimationsComplete);
        this.cachedMyHandAnimationsComplete = myHandAnimationsComplete;
        this.updateButtonStates();
      }
    });
    this.unsubscribers.push(unsubAnimation);

    // Subscribe to socket connection state
    // This ensures BET button is disabled when disconnected and re-enabled on reconnect
    const unsubSocket = useSocketStore.subscribe((state) => {
      const isConnected = state.isConnected;
      if (isConnected !== this.cachedIsConnected) {
        console.log('[PixiControlPanel] isConnected changed:', this.cachedIsConnected, '->', isConnected);
        this.cachedIsConnected = isConnected;
        this.updateButtonStates();
      }
    });
    this.unsubscribers.push(unsubSocket);

    // ===========================================
    // SERVER-AUTHORITATIVE CONTROLS SUBSCRIPTION
    // ===========================================
    // Server broadcasts exact button states for all players.
    // When server sends CONTROLS_UPDATED, update buttons immediately.
    const unsubControls = useGameStateStore.subscribe((state) => {
      const mySeatPosition = this.cachedMySeatPosition;
      if (mySeatPosition === null) return;

      const serverControls = state.seatControls.get(mySeatPosition) || null;
      // Only update if controls actually changed
      if (serverControls !== this.cachedServerControls) {
        console.log('[PixiControlPanel] Server controls updated:', serverControls);
        this.cachedServerControls = serverControls;
        // Clear the awaiting flag - server has responded
        this.awaitingServerControls = false;
        this.updateButtonStates();
      }
    });
    this.unsubscribers.push(unsubControls);
  }

  // ===========================================
  // RECOMMENDATION CALCULATION
  // ===========================================

  private computeRecommendation(): void {
    // EXTENSIVE DEBUG LOGGING for ALT+TAB issue
    const gameState = useGameStateStore.getState();
    console.log('[PixiControlPanel] computeRecommendation called, state:', {
      cachedShowRecommendations: this.cachedShowRecommendations,
      phase: gameState.phase,
      currentTurnSeat: gameState.currentTurnSeat,
      mySeatPosition: gameState.mySeatPosition,
      currentHandIndex: gameState.currentHandIndex,
    });

    // Reset if recommendations are disabled
    if (!this.cachedShowRecommendations) {
      console.log('[PixiControlPanel] computeRecommendation SKIP: recommendations disabled');
      this.cachedRecommendedAction = null;
      this.cachedIsDeviation = false;
      return;
    }

    const cardState = useCardTrackingStore.getState();

    // Must be in playing phase
    if (gameState.phase !== GamePhase.PLAYING) {
      console.log('[PixiControlPanel] computeRecommendation SKIP: not in playing phase, phase=', gameState.phase);
      this.cachedRecommendedAction = null;
      this.cachedIsDeviation = false;
      return;
    }

    // Must be our turn
    if (gameState.currentTurnSeat !== gameState.mySeatPosition) {
      console.log('[PixiControlPanel] computeRecommendation SKIP: not our turn, currentTurnSeat=', gameState.currentTurnSeat, 'mySeatPosition=', gameState.mySeatPosition);
      this.cachedRecommendedAction = null;
      this.cachedIsDeviation = false;
      return;
    }

    // Get player and hand data
    const player = gameState.players.get(gameState.mySeatPosition ?? -1);
    if (!player || !player.hands || player.hands.length === 0) {
      console.log('[PixiControlPanel] computeRecommendation SKIP: no player or hands');
      this.cachedRecommendedAction = null;
      this.cachedIsDeviation = false;
      return;
    }

    const handIndex = gameState.currentHandIndex ?? 0;
    const hand = player.hands[handIndex];
    if (!hand || !hand.cards || hand.cards.length === 0) {
      console.log('[PixiControlPanel] computeRecommendation SKIP: no hand or cards');
      this.cachedRecommendedAction = null;
      this.cachedIsDeviation = false;
      return;
    }

    // Skip if hand is resolved
    if (hand.outcome || hand.isBusted || hand.isDoubled) {
      console.log('[PixiControlPanel] computeRecommendation SKIP: hand already resolved', {
        outcome: hand.outcome,
        isBusted: hand.isBusted,
        isDoubled: hand.isDoubled,
      });
      this.cachedRecommendedAction = null;
      this.cachedIsDeviation = false;
      return;
    }

    // Calculate score from cards
    let score = 0;
    let aces = 0;
    for (const card of hand.cards) {
      if (card.rank === 'A') {
        aces++;
        score += 11;
      } else if (['K', 'Q', 'J'].includes(card.rank)) {
        score += 10;
      } else {
        score += parseInt(card.rank, 10);
      }
    }
    while (score > 21 && aces > 0) {
      score -= 10;
      aces--;
    }
    const isSoft = aces > 0 && score <= 21;

    // Skip if busted
    if (score > 21) {
      console.log('[PixiControlPanel] computeRecommendation SKIP: busted', {
        score,
        cardCount: hand.cards.length,
      });
      this.cachedRecommendedAction = null;
      this.cachedIsDeviation = false;
      return;
    }

    // If score is exactly 21, always STAND (no decision needed but auto-play needs a recommendation)
    if (score === 21) {
      console.log('[PixiControlPanel] Recommendation: STAND (score is 21)', {
        score,
        isSoft,
        cardCount: hand.cards.length,
      });
      this.cachedRecommendedAction = 'STAND';
      this.cachedIsDeviation = false;
      return;
    }

    // Get dealer upcard
    const dealerCards = gameState.dealer.visibleCards || gameState.dealer.cards;
    if (!dealerCards || dealerCards.length === 0) {
      console.log('[PixiControlPanel] computeRecommendation SKIP: no dealer cards', {
        visibleCards: gameState.dealer.visibleCards?.length,
        cards: gameState.dealer.cards?.length,
      });
      this.cachedRecommendedAction = null;
      this.cachedIsDeviation = false;
      return;
    }
    const dealerUpcard = dealerCards[0];

    // Determine available actions
    const canDouble = hand.cards.length === 2 && player.confirmedBet !== undefined;
    const canSplit = hand.cards.length === 2 &&
      hand.cards[0].rank === hand.cards[1].rank &&
      player.hands.length < 4;
    const canSurrender = hand.cards.length === 2 && player.hands.length === 1;

    // Get strategy recommendation
    const trueCount = cardState.trueCount;
    const cardsSeenCount = cardState.cardsSeenSinceShuffle?.length ?? 0;
    const penetration = gameState.penetration ?? 0;
    const useCountForDeviations = cardsSeenCount > 10 && penetration >= 50;

    const result = getBasicStrategy(
      { cards: hand.cards, score, isSoft },
      dealerUpcard,
      {
        canDouble,
        canSplit,
        canSurrender,
        trueCount: useCountForDeviations ? trueCount : undefined,
      }
    );

    this.cachedRecommendedAction = result.action;
    this.cachedIsDeviation = result.isDeviation ?? false;

    console.log(`[PixiControlPanel] Recommendation: action=${this.cachedRecommendedAction}, isDeviation=${this.cachedIsDeviation}, score=${score}, isSoft=${isSoft}`);
  }

  // ===========================================
  // LAYOUT BUILDING
  // ===========================================

  private buildLayout(): void {
    // Clear existing buttons and handles
    this.buttons.forEach((btn) => {
      btn.graphics.destroy();
      btn.text.destroy();
      btn.neonBorder?.destroy();
    });
    this.buttons.clear();

    // Destroy and reset text references
    this.autobetButtonText = null;
    this.betButtonText = null;
    // Destroy secondary text before clearing reference (prevents orphaned text)
    if (this.betButtonSecondaryText) {
      this.betButtonSecondaryText.destroy();
      this.betButtonSecondaryText = null;
    }

    this.leftHandle?.destroy();
    this.rightHandle?.destroy();
    this.bottomHandle?.destroy();
    this.topHandle?.destroy();
    this.leftHandle = null;
    this.rightHandle = null;
    this.bottomHandle = null;
    this.topHandle = null;

    if (this.panelPosition === 'bottom' || this.panelPosition === 'top') {
      this.buildHorizontalLayout();
    } else {
      this.buildVerticalLayout();
    }

    this.buildHandles();
    this.updateAutobetButtonText();
    this.updateButtonStates();
  }

  private buildHorizontalLayout(): void {
    const scale = this.config.scale;
    // Panel width matches table image width
    const panelWidth = this.config.tableBounds.width;
    const panelPadding = HORIZONTAL_PANEL_PADDING * scale;
    const sideStackWidth = SIDE_STACK_WIDTH * scale;
    const buttonGap = BUTTON_GAP * scale;
    const smallHeight = SMALL_BUTTON_HEIGHT * scale;
    const largeHeight = LARGE_BUTTON_HEIGHT * scale;

    // Fixed panel height matches vertical panel width for visual consistency
    const panelHeight = HORIZONTAL_PANEL_HEIGHT * scale;

    // Draw background
    // - top position: no rounded corners (flush with table below)
    // - bottom position: rounded top corners only (flush with table above)
    this.background.clear();
    if (this.panelPosition === 'top') {
      // No rounded corners for top position
      this.background.rect(0, 0, panelWidth, panelHeight);
      this.background.fill({ color: PANEL_BG_COLOR, alpha: PANEL_BG_ALPHA });
    } else {
      // Bottom position: rounded top corners, square bottom corners
      const radius = 16 * scale;
      this.background.moveTo(radius, 0);
      this.background.lineTo(panelWidth - radius, 0);
      this.background.arcTo(panelWidth, 0, panelWidth, radius, radius);
      this.background.lineTo(panelWidth, panelHeight);
      this.background.lineTo(0, panelHeight);
      this.background.lineTo(0, radius);
      this.background.arcTo(0, 0, radius, 0, radius);
      this.background.closePath();
      this.background.fill({ color: PANEL_BG_COLOR, alpha: PANEL_BG_ALPHA });
    }

    // Left stack: SPLIT, DOUBLE (side buttons)
    let y = panelPadding;
    this.createButton('SPLIT', 'SPLIT', panelPadding + HANDLE_WIDTH * scale, y, sideStackWidth, smallHeight, 'side');
    y += smallHeight + buttonGap;
    this.createButton('DOUBLE', 'DOUBLE', panelPadding + HANDLE_WIDTH * scale, y, sideStackWidth, smallHeight, 'side');

    // Center buttons: HIT, BET, STAND (main buttons)
    const centerX = panelPadding + HANDLE_WIDTH * scale + sideStackWidth + buttonGap;
    const centerWidth = panelWidth - (panelPadding * 2) - (HANDLE_WIDTH * scale * 2) - (sideStackWidth * 2) - (buttonGap * 2);
    const centerButtonWidth = (centerWidth - (buttonGap * 2)) / 3;
    const centerHeight = panelHeight - (panelPadding * 2);

    this.createButton('HIT', 'HIT', centerX, panelPadding, centerButtonWidth, centerHeight, 'main');
    this.createButton('BET', 'BET', centerX + centerButtonWidth + buttonGap, panelPadding, centerButtonWidth, centerHeight, 'main');
    this.createButton('STAND', 'STAND', centerX + (centerButtonWidth + buttonGap) * 2, panelPadding, centerButtonWidth, centerHeight, 'main');

    // Right stack: SURRENDER, AUTOBET (side buttons)
    const rightX = panelWidth - panelPadding - HANDLE_WIDTH * scale - sideStackWidth;
    y = panelPadding;
    this.createButton('SURRENDER', 'SURRENDER', rightX, y, sideStackWidth, smallHeight, 'side');
    y += smallHeight + buttonGap;
    this.createButton('AUTOBET', 'AUTOBET', rightX, y, sideStackWidth, smallHeight, 'side');
  }

  private buildVerticalLayout(): void {
    const scale = this.config.scale;
    const panelWidth = VERTICAL_PANEL_WIDTH * scale;
    const panelPadding = VERTICAL_PANEL_PADDING * scale;
    const buttonGap = BUTTON_GAP * scale;
    const smallHeight = SMALL_BUTTON_HEIGHT * scale;
    const handleSpace = HANDLE_CONTAINER_WIDTH * scale; // Space for handles at edges

    // Panel spans full canvas height (flush with header top, viewport bottom)
    const panelHeight = this.config.canvasHeight;

    // Calculate button width - leave room for handles at both edges
    const buttonWidth = panelWidth - (handleSpace * 2);

    // Fixed space taken by small buttons and gaps
    // 4 small buttons: SPLIT, DOUBLE, SURRENDER, AUTOBET
    // 6 gaps between 7 buttons
    // Bottom handle area
    const bottomHandleSpace = BOTTOM_HANDLE_CONTAINER_HEIGHT * scale;
    const fixedHeight = (smallHeight * 4) + (buttonGap * 6) + (panelPadding * 2) + bottomHandleSpace;

    // Remaining space distributed among 3 large buttons (HIT, BET, STAND)
    const remainingHeight = panelHeight - fixedHeight;
    const largeHeight = Math.max(smallHeight, remainingHeight / 3);

    // Draw background - full height, no rounded corners on top/bottom for flush fit
    this.background.clear();
    this.background.rect(0, 0, panelWidth, panelHeight);
    this.background.fill({ color: PANEL_BG_COLOR, alpha: PANEL_BG_ALPHA });

    // Center buttons horizontally - start after left handle area
    const buttonX = handleSpace;

    let y = panelPadding;

    // SPLIT - small (side button)
    this.createButton('SPLIT', 'SPLIT', buttonX, y, buttonWidth, smallHeight, 'side');
    y += smallHeight + buttonGap;

    // DOUBLE - small (side button)
    this.createButton('DOUBLE', 'DOUBLE', buttonX, y, buttonWidth, smallHeight, 'side');
    y += smallHeight + buttonGap;

    // HIT - large (main button)
    this.createButton('HIT', 'HIT', buttonX, y, buttonWidth, largeHeight, 'main');
    y += largeHeight + buttonGap;

    // BET - large (main button)
    this.createButton('BET', 'BET', buttonX, y, buttonWidth, largeHeight, 'main');
    y += largeHeight + buttonGap;

    // STAND - large (main button)
    this.createButton('STAND', 'STAND', buttonX, y, buttonWidth, largeHeight, 'main');
    y += largeHeight + buttonGap;

    // SURRENDER - small (side button)
    this.createButton('SURRENDER', 'SURRENDER', buttonX, y, buttonWidth, smallHeight, 'side');
    y += smallHeight + buttonGap;

    // AUTOBET - small (side button)
    this.createButton('AUTOBET', 'AUTOBET', buttonX, y, buttonWidth, smallHeight, 'side');
  }

  private createButton(
    id: string,
    label: string,
    x: number,
    y: number,
    width: number,
    height: number,
    buttonType: 'main' | 'side' = 'main'
  ): void {
    const colors = BUTTON_COLORS[id] || BUTTON_COLORS.BET;
    const scale = this.config.scale;

    // Create neon border graphics FIRST (so it's behind button)
    // Pre-create but keep hidden until needed
    const neonBorder = new Graphics();
    neonBorder.visible = false;
    this.addChild(neonBorder);

    // Create button graphics
    const graphics = new Graphics();
    graphics.roundRect(0, 0, width, height, BUTTON_RADIUS * scale);

    // Use gradient fills for specific buttons
    // Horizontal: left-to-right gradient, Vertical: top-to-bottom gradient
    // Continuous rainbow flow: each button picks up where the previous ended
    const isHorizontalLayout = this.panelPosition === 'top' || this.panelPosition === 'bottom';
    const gradientStart = { x: 0, y: 0 };
    const gradientEnd = isHorizontalLayout ? { x: 1, y: 0 } : { x: 0, y: 1 };

    if (id === 'SPLIT') {
      // #EF4444 (red) to #F35B2C
      const gradient = new FillGradient({
        type: 'linear',
        start: gradientStart,
        end: gradientEnd,
        colorStops: [
          { offset: 0, color: '#EF4444' },
          { offset: 1, color: '#F35B2C' },
        ],
      });
      graphics.fill(gradient);
    } else if (id === 'DOUBLE') {
      // #F35B2C to #F97316 (orange)
      const gradient = new FillGradient({
        type: 'linear',
        start: gradientStart,
        end: gradientEnd,
        colorStops: [
          { offset: 0, color: '#F35B2C' },
          { offset: 1, color: '#F97316' },
        ],
      });
      graphics.fill(gradient);
    } else if (id === 'HIT') {
      // #F97316 (orange) to #FBBF24 (yellow)
      const gradient = new FillGradient({
        type: 'linear',
        start: gradientStart,
        end: gradientEnd,
        colorStops: [
          { offset: 0, color: '#F97316' },
          { offset: 1, color: '#FBBF24' },
        ],
      });
      graphics.fill(gradient);
    } else if (id === 'STAND') {
      // #FBBF24 (yellow) to #9333EA (purple)
      const gradient = new FillGradient({
        type: 'linear',
        start: gradientStart,
        end: gradientEnd,
        colorStops: [
          { offset: 0, color: '#FBBF24' },
          { offset: 1, color: '#9333EA' },
        ],
      });
      graphics.fill(gradient);
    } else if (id === 'SURRENDER') {
      // #9333EA (purple) to #5A40E1
      const gradient = new FillGradient({
        type: 'linear',
        start: gradientStart,
        end: gradientEnd,
        colorStops: [
          { offset: 0, color: '#9333EA' },
          { offset: 1, color: '#5A40E1' },
        ],
      });
      graphics.fill(gradient);
    } else if (id === 'AUTOBET') {
      // #5A40E1 to #1D4ED8 (blue)
      const gradient = new FillGradient({
        type: 'linear',
        start: gradientStart,
        end: gradientEnd,
        colorStops: [
          { offset: 0, color: '#5A40E1' },
          { offset: 1, color: '#1D4ED8' },
        ],
      });
      graphics.fill(gradient);
    } else {
      // BET: Solid #FBBF24 (yellow)
      graphics.fill({ color: colors.from });
    }

    graphics.x = x;
    graphics.y = y;
    graphics.eventMode = 'static';
    graphics.cursor = 'pointer';

    // Create text - use config overrides if provided (percentage-based: 100 = 100%)
    const isHorizontal = this.panelPosition === 'top' || this.panelPosition === 'bottom';
    const textSizes = this.config.textSizes;
    const BASE_MAIN_FONT_SIZE = 20;
    const BASE_SIDE_FONT_SIZE = 12;
    let fontSize: number;

    if (buttonType === 'main') {
      const configPercent = isHorizontal ? textSizes?.mainButtonsHoriz : textSizes?.mainButtonsVert;
      const percent = configPercent !== undefined ? configPercent : 100;
      fontSize = (percent / 100) * BASE_MAIN_FONT_SIZE * scale;
    } else {
      const configPercent = isHorizontal ? textSizes?.sideButtonsHoriz : textSizes?.sideButtonsVert;
      const percent = configPercent !== undefined ? configPercent : 100;
      fontSize = (percent / 100) * BASE_SIDE_FONT_SIZE * scale;
    }

    const style = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize,
      fontWeight: 'bold',
      fill: colors.text,
    });
    const text = new Text({ text: label, style });
    text.anchor.set(0.5, 0.5);
    text.x = x + width / 2;
    text.y = y + height / 2;
    // Allow clicks to pass through text to button graphics
    text.eventMode = 'none';

    // Add click handler
    graphics.on('pointerdown', () => this.handleButtonClick(id));

    this.addChild(graphics);
    this.addChild(text);

    // Track text references for buttons that need dynamic text updates
    if (id === 'AUTOBET') {
      this.autobetButtonText = text;
    } else if (id === 'BET') {
      this.betButtonText = text;
    }

    this.buttons.set(id, {
      graphics,
      text,
      neonBorder,
      config: {
        label,
        action: id,
        gradientFrom: colors.from,
        gradientTo: colors.to,
        textColor: colors.text,
        size: height > SMALL_BUTTON_HEIGHT * this.config.scale * 1.5 ? 'large' : 'small',
      },
      enabled: true,
      recommended: false,
      isDeviation: false,
    });
  }

  private buildHandles(): void {
    const scale = this.config.scale;

    // Scaled handle dimensions (matching React panel)
    const handleContainerW = HANDLE_CONTAINER_WIDTH * scale;
    const handleContainerH = HANDLE_CONTAINER_HEIGHT * scale;
    const barW = HANDLE_BAR_WIDTH * scale;
    const barH = HANDLE_BAR_HEIGHT * scale;
    const bottomContainerW = BOTTOM_HANDLE_CONTAINER_WIDTH * scale;
    const bottomContainerH = BOTTOM_HANDLE_CONTAINER_HEIGHT * scale;
    const bottomBarW = BOTTOM_HANDLE_BAR_WIDTH * scale;
    const bottomBarH = BOTTOM_HANDLE_BAR_HEIGHT * scale;

    if (this.panelPosition === 'bottom' || this.panelPosition === 'top') {
      // Horizontal position: left and right handles to move to sides
      // Use actual panel width (matches table width)
      const panelWidth = this.config.tableBounds.width;
      const panelHeight = this.background.height;

      // Handles straddle the edge: 50% inside, 50% outside
      // Left handle - straddles left edge
      this.leftHandle = this.createHandle(
        -handleContainerW / 2,
        (panelHeight - handleContainerH) / 2,
        handleContainerW,
        handleContainerH,
        barW,
        barH,
        () => this.setPosition('table-left')
      );

      // Right handle - straddles right edge
      this.rightHandle = this.createHandle(
        panelWidth - handleContainerW / 2,
        (panelHeight - handleContainerH) / 2,
        handleContainerW,
        handleContainerH,
        barW,
        barH,
        () => this.setPosition('table-right')
      );

      // Top/bottom toggle handle - horizontal bar at opposite edge, straddling
      if (this.panelPosition === 'bottom') {
        // Top handle to move panel to top - straddles top edge
        this.topHandle = new Graphics();
        // Invisible hit area
        this.topHandle.roundRect(0, 0, bottomContainerW, bottomContainerH, 10 * scale);
        this.topHandle.fill({ color: 0x000000, alpha: 0 });
        // Visible bar - centered in container
        this.topHandle.roundRect(
          (bottomContainerW - bottomBarW) / 2,
          (bottomContainerH - bottomBarH) / 2,
          bottomBarW,
          bottomBarH,
          2 * scale
        );
        this.topHandle.fill({ color: HANDLE_COLOR, alpha: HANDLE_ALPHA });
        // Position straddling top edge
        this.topHandle.x = (panelWidth - bottomContainerW) / 2;
        this.topHandle.y = -bottomContainerH / 2;
        this.topHandle.eventMode = 'static';
        this.topHandle.cursor = 'pointer';
        this.topHandle.on('pointerdown', () => this.setPosition('top'));
        this.addChild(this.topHandle);
      } else {
        // Bottom handle to move panel to bottom - straddles bottom edge
        this.bottomHandle = new Graphics();
        // Invisible hit area
        this.bottomHandle.roundRect(0, 0, bottomContainerW, bottomContainerH, 10 * scale);
        this.bottomHandle.fill({ color: 0x000000, alpha: 0 });
        // Visible bar - centered in container
        this.bottomHandle.roundRect(
          (bottomContainerW - bottomBarW) / 2,
          (bottomContainerH - bottomBarH) / 2,
          bottomBarW,
          bottomBarH,
          2 * scale
        );
        this.bottomHandle.fill({ color: HANDLE_COLOR, alpha: HANDLE_ALPHA });
        // Position straddling bottom edge
        this.bottomHandle.x = (panelWidth - bottomContainerW) / 2;
        this.bottomHandle.y = panelHeight - bottomContainerH / 2;
        this.bottomHandle.eventMode = 'static';
        this.bottomHandle.cursor = 'pointer';
        this.bottomHandle.on('pointerdown', () => this.setPosition('bottom'));
        this.addChild(this.bottomHandle);
      }

    } else {
      // Vertical positions: handles for cycling through positions
      const positions: ControlPanelPosition[] = ['viewport-left', 'table-left', 'table-right', 'viewport-right'];
      const currentIdx = positions.indexOf(this.panelPosition);
      const panelWidth = VERTICAL_PANEL_WIDTH * scale;
      const panelHeight = this.background.height;

      // Handles straddle the edge: 50% inside, 50% outside
      // Left handle (move to previous position) - only show if not at leftmost
      if (currentIdx > 0) {
        this.leftHandle = this.createHandle(
          -handleContainerW / 2,
          (panelHeight - handleContainerH) / 2,
          handleContainerW,
          handleContainerH,
          barW,
          barH,
          () => this.setPosition(positions[currentIdx - 1])
        );
      }

      // Right handle (move to next position) - only show if not at rightmost
      if (currentIdx < positions.length - 1) {
        this.rightHandle = this.createHandle(
          panelWidth - handleContainerW / 2,
          (panelHeight - handleContainerH) / 2,
          handleContainerW,
          handleContainerH,
          barW,
          barH,
          () => this.setPosition(positions[currentIdx + 1])
        );
      }

      // Top handle (move to top position) - straddles top edge
      this.topHandle = new Graphics();
      // Invisible hit area
      this.topHandle.roundRect(0, 0, bottomContainerW, bottomContainerH, 10 * scale);
      this.topHandle.fill({ color: 0x000000, alpha: 0 });
      // Visible bar - centered in container
      this.topHandle.roundRect(
        (bottomContainerW - bottomBarW) / 2,
        (bottomContainerH - bottomBarH) / 2,
        bottomBarW,
        bottomBarH,
        2 * scale
      );
      this.topHandle.fill({ color: HANDLE_COLOR, alpha: HANDLE_ALPHA });
      // Position straddling top edge
      this.topHandle.x = (panelWidth - bottomContainerW) / 2;
      this.topHandle.y = -bottomContainerH / 2;
      this.topHandle.eventMode = 'static';
      this.topHandle.cursor = 'pointer';
      this.topHandle.on('pointerdown', () => this.setPosition('top'));
      this.addChild(this.topHandle);

      // Bottom handle (move to bottom position) - centered within bottom padding area
      // (exception: doesn't straddle edge in vertical orientation)
      this.bottomHandle = new Graphics();
      // Invisible hit area
      this.bottomHandle.roundRect(0, 0, bottomContainerW, bottomContainerH, 10 * scale);
      this.bottomHandle.fill({ color: 0x000000, alpha: 0 });
      // Visible bar - centered in container
      this.bottomHandle.roundRect(
        (bottomContainerW - bottomBarW) / 2,
        (bottomContainerH - bottomBarH) / 2,
        bottomBarW,
        bottomBarH,
        2 * scale
      );
      this.bottomHandle.fill({ color: HANDLE_COLOR, alpha: HANDLE_ALPHA });
      // Position centered within the bottom padding/margin area
      this.bottomHandle.x = (panelWidth - bottomContainerW) / 2;
      this.bottomHandle.y = panelHeight - bottomContainerH;
      this.bottomHandle.eventMode = 'static';
      this.bottomHandle.cursor = 'pointer';
      this.bottomHandle.on('pointerdown', () => this.setPosition('bottom'));
      this.addChild(this.bottomHandle);
    }
  }

  private createHandle(
    x: number,
    y: number,
    width: number,
    height: number,
    barWidth: number,
    barHeight: number,
    onClick: () => void
  ): Graphics {
    const handle = new Graphics();

    // Invisible hit area
    handle.rect(0, 0, width, height);
    handle.fill({ color: 0x000000, alpha: 0 });

    // Visible bar
    handle.roundRect((width - barWidth) / 2, (height - barHeight) / 2, barWidth, barHeight, 2);
    handle.fill({ color: HANDLE_COLOR, alpha: HANDLE_ALPHA });

    handle.x = x;
    handle.y = y;
    handle.eventMode = 'static';
    handle.cursor = 'pointer';
    handle.on('pointerdown', onClick);

    this.addChild(handle);
    return handle;
  }

  // ===========================================
  // BUTTON STATE MANAGEMENT
  // ===========================================

  private updateButtonStates(): void {
    // If awaiting server response after an action, keep buttons disabled
    // This prevents other subscriptions from re-enabling buttons before CONTROLS_UPDATED arrives
    if (this.awaitingServerControls) {
      console.log('[PixiControlPanel] Awaiting server controls - keeping buttons disabled');
      return;
    }

    const stagedAmount = this.cachedStagedAmount;
    const balance = this.cachedBalance;
    const serverControls = this.cachedServerControls;

    // ===========================================
    // SERVER-AUTHORITATIVE BUTTON STATES
    // ===========================================
    // When server provides controls, use them directly.
    // Client should never "derive" button states - only apply server instructions.
    // The bet button has special handling for autobet mode and staged amount validation.

    if (serverControls) {
      // Use server-authoritative controls for action buttons
      this.setButtonEnabled('HIT', serverControls.hit);
      this.setButtonEnabled('STAND', serverControls.stand);
      this.setButtonEnabled('DOUBLE', serverControls.double);
      this.setButtonEnabled('SPLIT', serverControls.split);
      this.setButtonEnabled('SURRENDER', serverControls.surrender);
      // Insurance button state from server
      // TODO: Wire up insurance panel to use serverControls.insurance

      // BET button: server says if betting is allowed, but we still need to check:
      // - Socket connection (prevent dropped messages)
      // - Staged amount validation (must have chips staged and within balance)
      // - Autobet mode (always enabled as STOP button)
      // - Chip resolution animations complete (wait for payout/collection to finish)
      const serverBetEnabled = serverControls.bet;
      const clientBetValid = stagedAmount > 0 && stagedAmount <= balance;
      const chipsResolved = this.cachedMyHandAnimationsComplete;
      const betEnabled = this.cachedIsConnected && chipsResolved && (this.cachedAutobetEnabled ? true : (serverBetEnabled && clientBetValid));
      this.setButtonEnabled('BET', betEnabled);

      console.log('[PixiControlPanel] Server-authoritative button states:', {
        serverControls,
        clientBetValid,
        isConnected: this.cachedIsConnected,
        autobetEnabled: this.cachedAutobetEnabled,
        chipsResolved,
        betEnabled,
      });
    } else {
      // No server controls yet (initial load, reconnection, etc.)
      // Disable all action buttons - only BET/AUTOBET have client-side enable logic
      this.setButtonEnabled('HIT', false);
      this.setButtonEnabled('STAND', false);
      this.setButtonEnabled('DOUBLE', false);
      this.setButtonEnabled('SPLIT', false);
      this.setButtonEnabled('SURRENDER', false);

      // BET button: allow if staged amount is valid and connected
      const clientBetValid = stagedAmount > 0 && stagedAmount <= balance;
      const chipsResolved = this.cachedMyHandAnimationsComplete;
      const betEnabled = this.cachedIsConnected && chipsResolved && (this.cachedAutobetEnabled ? true : clientBetValid);
      this.setButtonEnabled('BET', betEnabled);

      console.log('[PixiControlPanel] No server controls - action buttons disabled, bet:', { betEnabled });
    }

    // AUTOBET button always follows staged amount (client-only, not server-controlled)
    this.setButtonEnabled('AUTOBET', stagedAmount > 0);

    // Update neon borders for recommendations
    this.updateNeonBorders();
  }

  private updateNeonBorders(): void {
    // Map strategy action to button ID (StrategyAction uses uppercase)
    const actionToButtonId: Record<StrategyAction, string> = {
      'HIT': 'HIT',
      'STAND': 'STAND',
      'DOUBLE': 'DOUBLE',
      'SPLIT': 'SPLIT',
      'SURRENDER': 'SURRENDER',
      'INSURANCE': 'INSURANCE',
    };

    const recommendedButtonId = this.cachedRecommendedAction
      ? actionToButtonId[this.cachedRecommendedAction]
      : null;

    console.log(`[PixiControlPanel] updateNeonBorders: action=${this.cachedRecommendedAction}, buttonId=${recommendedButtonId}, showRec=${this.cachedShowRecommendations}, btnCount=${this.buttons.size}`);

    // Update each button's neon border visibility and state
    this.buttons.forEach((button, id) => {
      const isRecommended = this.cachedShowRecommendations &&
        recommendedButtonId === id &&
        button.enabled;

      if (id === recommendedButtonId) {
        console.log(`[PixiControlPanel] Button ${id}: isRecommended=${isRecommended}, enabled=${button.enabled}, hasNeonBorder=${!!button.neonBorder}, gfxBounds=${button.graphics ? `${button.graphics.x},${button.graphics.y},${button.graphics.width},${button.graphics.height}` : 'null'}`);
      }

      if (isRecommended && button.neonBorder) {
        // Set state - the animation ticker will handle actual drawing
        button.neonBorder.visible = true;
        button.recommended = true;
        button.isDeviation = this.cachedIsDeviation;
        // Trigger immediate draw via animation
        this.animateNeonBorders();
      } else if (button.neonBorder) {
        // Hide neon border
        button.neonBorder.clear();
        button.neonBorder.visible = false;
        button.recommended = false;
        button.isDeviation = false;
      }
    });
  }

  private setButtonEnabled(id: string, enabled: boolean): void {
    const button = this.buttons.get(id);
    if (!button) return;

    button.enabled = enabled;
    button.graphics.alpha = enabled ? 1 : DISABLED_ALPHA;
    button.text.alpha = enabled ? 1 : DISABLED_ALPHA;
    button.graphics.cursor = enabled ? 'pointer' : 'default';
  }

  /**
   * Disable all action buttons immediately.
   * Called when user clicks any action - control panel goes "dead" until
   * server responds with CONTROLS_UPDATED containing new button states.
   */
  private disableAllActionButtons(): void {
    console.log('[PixiControlPanel] Disabling all action buttons (waiting for server)');
    // Set flag to prevent updateButtonStates from re-enabling until server responds
    this.awaitingServerControls = true;
    this.setButtonEnabled('HIT', false);
    this.setButtonEnabled('STAND', false);
    this.setButtonEnabled('DOUBLE', false);
    this.setButtonEnabled('SPLIT', false);
    this.setButtonEnabled('SURRENDER', false);
    this.setButtonEnabled('BET', false);
    // Note: AUTOBET is client-only toggle, doesn't need server confirmation

    // CRITICAL: Immediately clear all neon borders when disabling buttons
    // This prevents visual artifacts (diagonal slashes) from the neon animation
    // running while button states are in flux
    this.buttons.forEach((button) => {
      if (button.neonBorder) {
        button.neonBorder.clear();
        button.neonBorder.visible = false;
        button.recommended = false;
        button.isDeviation = false;
      }
    });
  }

  /**
   * Update AUTOBET and BET button text and appearance based on autobet state
   */
  private updateAutobetButtonText(): void {
    console.log('[PixiControlPanel] updateAutobetButtonText called, autobetEnabled:', this.cachedAutobetEnabled, 'hasButtonText:', !!this.autobetButtonText);
    // Update AUTOBET button text
    if (this.autobetButtonText) {
      const newText = this.cachedAutobetEnabled ? 'AUTO ON' : 'AUTOBET';
      console.log('[PixiControlPanel] Setting autobet button text to:', newText);
      this.autobetButtonText.text = newText;
    }

    // Update BET button - shows "STOP" + "AUTOBET" (two lines, different sizes) when autobet is active
    const betButton = this.buttons.get('BET');
    if (betButton && this.betButtonText) {
      const scale = this.config.scale;
      const graphics = betButton.graphics;
      const isHorizontal = this.panelPosition === 'top' || this.panelPosition === 'bottom';
      const textSizes = this.config.textSizes;

      // Get button dimensions BEFORE clearing (bounds returns the drawn area)
      const bounds = graphics.getLocalBounds();
      const buttonWidth = bounds.width;
      const buttonHeight = bounds.height;
      const buttonX = graphics.x;
      const buttonY = graphics.y;

      // Always clean up secondary text first
      if (this.betButtonSecondaryText) {
        this.betButtonSecondaryText.destroy();
        this.betButtonSecondaryText = null;
      }

      // Calculate font sizes
      const BASE_MAIN_FONT_SIZE = 18;
      const BASE_SIDE_FONT_SIZE = 12;
      const mainConfigPercent = isHorizontal ? textSizes?.mainButtonsHoriz : textSizes?.mainButtonsVert;
      const sideConfigPercent = isHorizontal ? textSizes?.sideButtonsHoriz : textSizes?.sideButtonsVert;
      const mainPercent = mainConfigPercent !== undefined ? mainConfigPercent : 100;
      const sidePercent = sideConfigPercent !== undefined ? sideConfigPercent : 100;
      const stopFontSize = (mainPercent / 100) * BASE_MAIN_FONT_SIZE * scale; // Same as BET
      const autobetFontSize = (sidePercent / 100) * BASE_SIDE_FONT_SIZE * scale; // Same as AUTOBET button

      if (this.cachedAutobetEnabled) {
        // AUTOBET ON: Change to solid blue fill with "STOP" (large) + "AUTOBET" (small)

        // Update primary text to "STOP" with BET font size
        this.betButtonText.text = 'STOP';
        this.betButtonText.style.fill = 0xffffff;
        this.betButtonText.style.fontSize = stopFontSize;

        // Create secondary "AUTOBET" text with side button font size
        const secondaryStyle = new TextStyle({
          fontFamily: 'Arial, sans-serif',
          fontSize: autobetFontSize,
          fontWeight: 'bold',
          fill: 0xffffff,
        });
        this.betButtonSecondaryText = new Text({ text: 'AUTOBET', style: secondaryStyle });
        this.betButtonSecondaryText.anchor.set(0.5, 0.5);
        this.betButtonSecondaryText.eventMode = 'none';
        this.addChild(this.betButtonSecondaryText);

        // Redraw button with solid #1D4ED8
        graphics.clear();
        graphics.roundRect(0, 0, buttonWidth, buttonHeight, BUTTON_RADIUS * scale);
        graphics.fill({ color: 0xEF4343 });

        // Position both texts vertically centered with single line spacing
        const buttonCenterX = buttonX + buttonWidth / 2;
        const buttonCenterY = buttonY + buttonHeight / 2;
        const lineGap = 0; // Single-spaced (no extra gap)
        const primaryHeight = this.betButtonText.height;
        const secondaryHeight = this.betButtonSecondaryText.height;
        const totalHeight = primaryHeight + lineGap + secondaryHeight;

        this.betButtonText.x = buttonCenterX;
        this.betButtonText.y = buttonCenterY - totalHeight / 2 + primaryHeight / 2;
        this.betButtonSecondaryText.x = buttonCenterX;
        this.betButtonSecondaryText.y = buttonCenterY + totalHeight / 2 - secondaryHeight / 2;

      } else {
        // AUTOBET OFF: Restore gradient fill and single "BET" text with black text
        this.betButtonText.text = 'BET';
        this.betButtonText.style.fill = 0x000000;
        this.betButtonText.style.fontSize = stopFontSize; // Same size as STOP

        // Redraw button with original gradient (BET uses default solid color)
        graphics.clear();
        graphics.roundRect(0, 0, buttonWidth, buttonHeight, BUTTON_RADIUS * scale);
        graphics.fill({ color: betButton.config.gradientFrom });

        // Re-center the primary text
        const buttonCenterX = buttonX + buttonWidth / 2;
        const buttonCenterY = buttonY + buttonHeight / 2;
        this.betButtonText.x = buttonCenterX;
        this.betButtonText.y = buttonCenterY;
      }
    }

    console.log('[PixiControlPanel] Autobet button text updated:', {
      autobetEnabled: this.cachedAutobetEnabled,
      autobetText: this.autobetButtonText?.text,
      betText: this.betButtonText?.text,
    });
  }

  // ===========================================
  // EVENT HANDLERS
  // ===========================================

  private handleButtonClick(id: string): void {
    const button = this.buttons.get(id);
    console.log('[PixiControlPanel] Button clicked:', id, 'enabled:', button?.enabled);

    if (!button || !button.enabled) {
      console.log('[PixiControlPanel] Button blocked - not enabled');
      return;
    }

    switch (id) {
      case 'HIT':
        // Disable all buttons immediately - server will re-enable via CONTROLS_UPDATED
        this.disableAllActionButtons();
        console.log('[PixiControlPanel] Calling onHit');
        this.config.onHit();
        break;
      case 'STAND':
        this.disableAllActionButtons();
        console.log('[PixiControlPanel] Calling onStand');
        this.config.onStand();
        break;
      case 'DOUBLE':
        this.disableAllActionButtons();
        console.log('[PixiControlPanel] Calling onDouble');
        this.config.onDouble();
        break;
      case 'SPLIT':
        this.disableAllActionButtons();
        console.log('[PixiControlPanel] Calling onSplit');
        this.config.onSplit();
        break;
      case 'SURRENDER':
        this.disableAllActionButtons();
        console.log('[PixiControlPanel] Calling onSurrender');
        this.config.onSurrender();
        break;
      case 'BET':
        // When autobet is enabled, BET becomes STOP (toggles autobet off)
        if (this.cachedAutobetEnabled) {
          console.log('[PixiControlPanel] Calling onAutobet (STOP)');
          this.config.onAutobet();
        } else {
          // Disable all buttons - server will re-enable via CONTROLS_UPDATED
          this.disableAllActionButtons();
          console.log('[PixiControlPanel] Calling onBet');
          this.config.onBet();
        }
        break;
      case 'AUTOBET':
        console.log('[PixiControlPanel] Calling onAutobet');
        this.config.onAutobet();
        break;
    }
  }

  // ===========================================
  // POSITION MANAGEMENT
  // ===========================================

  public setPosition(newPosition: ControlPanelPosition): void {
    if (newPosition === this.panelPosition) return;

    this.panelPosition = newPosition;
    storePosition(this.config.tableId, newPosition);

    // Rebuild layout for new position
    this.buildLayout();
    this.updatePanelPosition();

    // Notify callback
    this.config.onPositionChange?.(newPosition);
  }

  public getPosition(): ControlPanelPosition {
    return this.panelPosition;
  }

  private updatePanelPosition(): void {
    const { canvasWidth, canvasHeight, tableBounds, scale } = this.config;
    const verticalPanelWidth = VERTICAL_PANEL_WIDTH * scale;

    console.log('[PixiControlPanel] updatePanelPosition:', {
      position: this.panelPosition,
      canvasWidth,
      canvasHeight,
      tableBounds,
      scale,
      verticalPanelWidth,
      backgroundHeight: this.background.height,
    });

    switch (this.panelPosition) {
      case 'top':
        // Align with table image (same width), top edge at top of canvas
        this.x = tableBounds.x;
        this.y = 0;
        break;

      case 'bottom':
        // Align with table image (same width), bottom edge flush with viewport bottom
        this.x = tableBounds.x;
        this.y = canvasHeight - this.background.height;
        break;

      case 'viewport-left':
        // Panel's LEFT edge flush with viewport LEFT
        this.x = 0;
        this.y = 0;
        break;

      case 'table-left':
        // Panel's RIGHT edge flush with table LEFT edge
        // Clamp to viewport bounds (don't go off-screen left)
        this.x = Math.max(0, tableBounds.x - verticalPanelWidth);
        this.y = 0;
        break;

      case 'table-right':
        // Panel's LEFT edge flush with table RIGHT edge
        // Clamp to viewport bounds (don't go off-screen right)
        this.x = Math.min(canvasWidth - verticalPanelWidth, tableBounds.x + tableBounds.width);
        this.y = 0;
        break;

      case 'viewport-right':
        // Panel's RIGHT edge flush with viewport RIGHT
        this.x = canvasWidth - verticalPanelWidth;
        this.y = 0;
        break;
    }

    console.log('[PixiControlPanel] Final position:', { x: this.x, y: this.y });
  }

  // ===========================================
  // PUBLIC API
  // ===========================================

  /**
   * Update configuration (e.g., when canvas resizes)
   */
  public updateConfig(config: Partial<PixiControlPanelConfig>): void {
    Object.assign(this.config, config);
    this.buildLayout();
    this.updatePanelPosition();
  }

  /**
   * Force refresh of button states
   */
  public refresh(): void {
    this.updateButtonStates();
  }

  /**
   * Update socket connection state
   * Disables bet button when not connected to prevent dropped messages
   */
  public setIsConnected(isConnected: boolean): void {
    if (this.cachedIsConnected !== isConnected) {
      this.cachedIsConnected = isConnected;
      this.updateButtonStates();
    }
  }

  // ===========================================
  // AUTO-PLAY (Imperative, no React race conditions)
  // ===========================================

  /**
   * Enable or disable auto-play.
   * When enabled, automatically executes recommended actions after a delay.
   */
  public setAutoPlayEnabled(enabled: boolean): void {
    console.log('[PixiControlPanel] setAutoPlayEnabled:', enabled);
    this.autoPlayEnabled = enabled;

    if (!enabled) {
      // Clear any pending timer when disabled
      this.clearAutoPlayTimer();
    } else {
      // Try to start auto-play if conditions are met
      this.tryAutoPlay();
    }
  }

  /**
   * Force retry autoplay after tab visibility change.
   * This recomputes recommendations and resets the executed flag to allow
   * autoplay to resume after ALT+TAB.
   */
  public forceRetryAutoPlay(): void {
    console.log('[PixiControlPanel] forceRetryAutoPlay called');

    // Clear any pending timer
    this.clearAutoPlayTimer();

    // Reset the executed flag so we can try again
    this.autoPlayActionExecutedThisTurn = false;

    // CRITICAL: Refresh cached state from stores before computing recommendations
    // After a keyed resync, the cached values may be stale
    const gameState = useGameStateStore.getState();
    const authState = useAuthStore.getState();
    const stagedState = useStagedChipsStore.getState();
    const myPlayer = gameState.players.get(gameState.mySeatPosition ?? -1);

    this.cachedAvailableActions = [...(gameState.availableActions || [])] as string[];
    this.cachedGamePhase = gameState.phase;
    this.cachedCurrentBet = myPlayer?.confirmedBet || 0;
    this.cachedBalance = authState.user?.balance || 0;
    this.cachedStagedAmount = stagedState.stagedAmount;
    this.cachedAutobetEnabled = stagedState.autobetEnabled;
    this.cachedCurrentTurnSeat = gameState.currentTurnSeat;
    this.cachedCurrentHandIndex = gameState.currentHandIndex;
    this.cachedMySeatPosition = gameState.mySeatPosition;
    this.cachedShowRecommendations = authState.user?.showRecommendations ?? false;
    this.autoPlayEnabled = authState.user?.autoPlay ?? false;
    this.cachedTurnSignal = gameState.turnSignal;
    this.cachedIsConnected = useSocketStore.getState().isConnected;
    // Refresh server-authoritative controls
    this.cachedServerControls = gameState.seatControls.get(gameState.mySeatPosition ?? -1) || null;

    console.log('[PixiControlPanel] forceRetryAutoPlay refreshed state:', {
      phase: this.cachedGamePhase,
      mySeatPosition: this.cachedMySeatPosition,
      currentTurnSeat: this.cachedCurrentTurnSeat,
      availableActions: this.cachedAvailableActions,
      autoPlayEnabled: this.autoPlayEnabled,
      currentBet: this.cachedCurrentBet,
    });

    // Recompute recommendation with fresh state
    this.computeRecommendation();

    // Log state after recompute
    console.log('[PixiControlPanel] forceRetryAutoPlay state after recompute:', {
      autoPlayEnabled: this.autoPlayEnabled,
      cachedRecommendedAction: this.cachedRecommendedAction,
      cachedAvailableActions: this.cachedAvailableActions,
      cachedGamePhase: this.cachedGamePhase,
    });

    // Update button states with fresh values
    this.updateButtonStates();

    // Try autoplay
    if (this.autoPlayEnabled) {
      this.tryAutoPlay();
    }
  }

  /**
   * Get the current computed recommendation.
   * Used by PixiTablePage for post-resync autoplay execution.
   */
  public getComputedRecommendation(): { action: StrategyAction; isDeviation: boolean } | null {
    if (!this.cachedRecommendedAction) {
      return null;
    }
    return {
      action: this.cachedRecommendedAction,
      isDeviation: this.cachedIsDeviation,
    };
  }

  /**
   * Check if we should start auto-play timer and execute action.
   * Called when state changes or auto-play is enabled.
   *
   * TODO: MARKED FOR DELETION - Server-side autoplay now handles this.
   * Keeping for reference/fallback until server-side autoplay is fully tested.
   */
  private tryAutoPlay(): void {
    // SERVER-SIDE AUTOPLAY: This client-side logic is now disabled.
    // The server handles autoplay execution when turn starts.
    // Keeping the method signature to avoid breaking callers.
    console.log('[PixiControlPanel] tryAutoPlay DISABLED - server handles autoplay');
  }

  /**
   * Execute the recommended action.
   * Called when auto-play timer fires.
   *
   * TODO: MARKED FOR DELETION - Server-side autoplay now handles this.
   */
  private executeAutoPlayAction(): void {
    // SERVER-SIDE AUTOPLAY: This client-side logic is now disabled.
    console.log('[PixiControlPanel] executeAutoPlayAction DISABLED - server handles autoplay');
  }

  /**
   * Clear any pending auto-play timer.
   */
  private clearAutoPlayTimer(): void {
    if (this.autoPlayTimer) {
      clearTimeout(this.autoPlayTimer);
      this.autoPlayTimer = null;
    }
  }

  /**
   * Check if turn has changed and reset action-executed flag.
   * Called from store subscription when turn state changes.
   */
  private checkAutoPlayTurnChange(): void {
    const newTurnKey = `${this.cachedCurrentTurnSeat}-${this.cachedCurrentHandIndex}`;

    if (newTurnKey !== this.autoPlayLastTurnKey) {
      console.log('[PixiControlPanel] Auto-play: turn changed from', this.autoPlayLastTurnKey, 'to', newTurnKey);
      this.autoPlayLastTurnKey = newTurnKey;
      this.autoPlayActionExecutedThisTurn = false;
      this.clearAutoPlayTimer();

      // Try to start auto-play for new turn
      this.tryAutoPlay();
    }
  }

  // ===========================================
  // INSURANCE AUTO-PLAY
  // ===========================================

  /**
   * Compute insurance recommendation based on true count.
   * Insurance is recommended when true count >= +3 (deck rich in 10s).
   */
  private computeInsuranceRecommendation(): void {
    if (!this.cachedShowRecommendations) {
      this.cachedInsuranceRecommendation = null;
      return;
    }

    const cardState = useCardTrackingStore.getState();
    const gameState = useGameStateStore.getState();
    const trueCount = cardState.trueCount;
    const cardsSeenCount = cardState.cardsSeenSinceShuffle?.length ?? 0;
    const penetration = gameState.penetration ?? 0;

    // Only use count for insurance if we have enough cards seen and penetration
    const useCount = cardsSeenCount > 10 && penetration >= 50;

    if (useCount && trueCount >= 3) {
      this.cachedInsuranceRecommendation = 'ACCEPT';
      console.log('[PixiControlPanel] Insurance recommendation: ACCEPT (TC=' + trueCount.toFixed(1) + ')');
    } else {
      this.cachedInsuranceRecommendation = 'DECLINE';
      console.log('[PixiControlPanel] Insurance recommendation: DECLINE (TC=' + (useCount ? trueCount.toFixed(1) : 'N/A') + ')');
    }
  }

  /**
   * Try to start insurance auto-play timer.
   * Called when insurance is offered or recommendation changes.
   *
   * TODO: MARKED FOR DELETION - Server-side autoplay now handles insurance decisions.
   */
  private tryInsuranceAutoPlay(): void {
    // SERVER-SIDE AUTOPLAY: This client-side logic is now disabled.
    console.log('[PixiControlPanel] tryInsuranceAutoPlay DISABLED - server handles insurance autoplay');
  }

  /**
   * Execute the insurance decision.
   * Called when insurance auto-play timer fires.
   *
   * TODO: MARKED FOR DELETION - Server-side autoplay now handles insurance decisions.
   */
  private executeInsuranceAutoPlay(): void {
    // SERVER-SIDE AUTOPLAY: This client-side logic is now disabled.
    console.log('[PixiControlPanel] executeInsuranceAutoPlay DISABLED - server handles insurance autoplay');
  }

  /**
   * Clear any pending insurance auto-play timer.
   */
  private clearInsuranceAutoPlayTimer(): void {
    if (this.insuranceAutoPlayTimer) {
      clearTimeout(this.insuranceAutoPlayTimer);
      this.insuranceAutoPlayTimer = null;
    }
  }

  /**
   * Cleanup - call when removing from scene
   */
  public dispose(): void {
    // Clear auto-play timers
    this.clearAutoPlayTimer();
    this.clearInsuranceAutoPlayTimer();
    // Stop neon animation
    this.stopNeonAnimation();

    // Remove keyboard handler
    this.removeKeyboardHandler();

    // Unsubscribe from stores
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];

    // Destroy children
    this.buttons.forEach((btn) => {
      btn.graphics.destroy();
      btn.text.destroy();
      btn.neonBorder?.destroy();
    });
    this.buttons.clear();

    // Destroy secondary text if it exists
    if (this.betButtonSecondaryText) {
      this.betButtonSecondaryText.destroy();
      this.betButtonSecondaryText = null;
    }

    this.leftHandle?.destroy();
    this.rightHandle?.destroy();
    this.bottomHandle?.destroy();
    this.topHandle?.destroy();

    this.background.destroy();
    this.destroy();
  }
}

// ===========================================
// FACTORY FUNCTION
// ===========================================

export function createPixiControlPanel(config: PixiControlPanelConfig): PixiControlPanel {
  return new PixiControlPanel(config);
}
